/**
 * Universities domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (with a
 * hand-written detail wrapper where the legacy hook shape relied on a
 * 404-skipping retry predicate).
 *
 * Write hooks go through `useInvalidatingMutation` so cache invalidation
 * is declarative and compile-time-enforced via the `UniversityMutationId`
 * registry in `./invalidation`.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListUniversities,
  listUniversitiesQueryKey,
  useGetUniversitiesSummary,
  getUniversitiesSummaryQueryKey,
  getUniversityQueryKey,
  useListUniversityFoodManagementCompanies,
  listUniversityFoodManagementCompaniesQueryKey,
} from '../generated/hooks/universitiesController'

// ── Generated client-function re-exports (imperative consumers) ────

export { listUniversities } from '../generated/clients/listUniversities'
export { getUniversitiesSummary } from '../generated/clients/getUniversitiesSummary'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  ListUniversitiesQueryResponse,
  ListUniversitiesQueryParams,
} from '../generated/types/ListUniversities'
export type {
  GetUniversityQueryResponse,
  GetUniversityPathParams,
} from '../generated/types/GetUniversity'
export type { GetUniversitiesSummaryQueryResponse } from '../generated/types/GetUniversitiesSummary'
export type { ListUniversityFoodManagementCompaniesQueryResponse } from '../generated/types/ListUniversityFoodManagementCompanies'
export type {
  BulkTagsUniversitiesMutationRequest,
  BulkTagsUniversitiesMutationResponse,
} from '../generated/types/BulkTagsUniversities'
export type {
  BulkDistributorsUniversitiesMutationRequest,
  BulkDistributorsUniversitiesMutationResponse,
} from '../generated/types/BulkDistributorsUniversities'
export type {
  BulkGposUniversitiesMutationRequest,
  BulkGposUniversitiesMutationResponse,
} from '../generated/types/BulkGposUniversities'
export type {
  BulkDealsUniversitiesMutationRequest,
  BulkDealsUniversitiesMutationResponse,
} from '../generated/types/BulkDealsUniversities'
export type {
  AssignUniversitiesToCampaignMutationRequest,
  AssignUniversitiesToCampaignMutationResponse,
} from '../generated/types/AssignUniversitiesToCampaign'
export type {
  ExportUniversitiesV2MutationRequest,
  ExportUniversitiesV2MutationResponse,
} from '../generated/types/ExportUniversitiesV2'

// ── Internal imports for hand-written wrappers + mutations ─────────

import { getUniversityQueryKey as _getUniversityQueryKey } from '../generated/hooks/universitiesController'
import { getUniversity } from '../generated/clients/getUniversity'
import { bulkTagsUniversities } from '../generated/clients/bulkTagsUniversities'
import { bulkDistributorsUniversities } from '../generated/clients/bulkDistributorsUniversities'
import { bulkGposUniversities } from '../generated/clients/bulkGposUniversities'
import { bulkDealsUniversities } from '../generated/clients/bulkDealsUniversities'
import { assignUniversitiesToCampaign } from '../generated/clients/assignUniversitiesToCampaign'
import { exportUniversitiesV2 } from '../generated/clients/exportUniversitiesV2'

import type {
  GetUniversityQueryResponse,
  GetUniversity404,
} from '../generated/types/GetUniversity'
import type { BulkTagsUniversitiesMutationRequest } from '../generated/types/BulkTagsUniversities'
import type { BulkDistributorsUniversitiesMutationRequest } from '../generated/types/BulkDistributorsUniversities'
import type { BulkGposUniversitiesMutationRequest } from '../generated/types/BulkGposUniversities'
import type { BulkDealsUniversitiesMutationRequest } from '../generated/types/BulkDealsUniversities'
import type { AssignUniversitiesToCampaignMutationRequest } from '../generated/types/AssignUniversitiesToCampaign'
import type { ExportUniversitiesV2MutationRequest } from '../generated/types/ExportUniversitiesV2'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * Get university detail.
 *
 * Hand-written wrapper around the generated `getUniversity` client because
 * the legacy hook skips retries on 404 — without that predicate, React
 * Query retries 3× before surrendering, which delays "not found" feedback
 * in the UI. The wrapper accepts an optional id (so callers can pass
 * `undefined` before route resolution) and disables the query until the
 * id arrives.
 */
export function useGetUniversity(
  universityId?: number | string,
  options: {
    query?: Omit<
      UseQueryOptions<GetUniversityQueryResponse, AxiosError<GetUniversity404>>,
      'queryKey' | 'queryFn' | 'enabled' | 'retry'
    >
  } = {}
) {
  const { query = {} } = options
  // The backend route is `<str:university_id>`; callers pass the numeric
  // ContactCompany id, so coerce to the string the generated client expects.
  const id = universityId != null ? String(universityId) : undefined
  return useQuery<GetUniversityQueryResponse, AxiosError<GetUniversity404>>({
    queryKey:
      id !== undefined
        ? _getUniversityQueryKey({ university_id: id })
        : ['university', universityId],
    queryFn: async ({ signal }) =>
      getUniversity({ university_id: id as string }, { signal }),
    enabled: id !== undefined,
    retry: (failureCount, error) =>
      axios.isAxiosError(error) && error?.response?.status === 404
        ? false
        : failureCount < 3,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useBulkTagsUniversities() {
  return useInvalidatingMutation('bulkTagsUniversities', invalidationRegistry, {
    mutationFn: (variables: { data: BulkTagsUniversitiesMutationRequest }) =>
      bulkTagsUniversities(variables.data),
  })
}

export function useBulkDistributorsUniversities() {
  return useInvalidatingMutation(
    'bulkDistributorsUniversities',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: BulkDistributorsUniversitiesMutationRequest
      }) => bulkDistributorsUniversities(variables.data),
    }
  )
}

export function useBulkGposUniversities() {
  return useInvalidatingMutation('bulkGposUniversities', invalidationRegistry, {
    mutationFn: (variables: { data: BulkGposUniversitiesMutationRequest }) =>
      bulkGposUniversities(variables.data),
  })
}

export function useBulkDealsUniversities() {
  return useInvalidatingMutation(
    'bulkDealsUniversities',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: BulkDealsUniversitiesMutationRequest }) =>
        bulkDealsUniversities(variables.data),
    }
  )
}

export function useAssignUniversitiesToCampaign() {
  return useInvalidatingMutation(
    'assignUniversitiesToCampaign',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: AssignUniversitiesToCampaignMutationRequest
      }) => assignUniversitiesToCampaign(variables.data),
    }
  )
}

export function useExportUniversitiesV2() {
  return useInvalidatingMutation('exportUniversitiesV2', invalidationRegistry, {
    mutationFn: (variables: { data: ExportUniversitiesV2MutationRequest }) =>
      exportUniversitiesV2(variables.data),
  })
}
